import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { useDeepgram } from '@/features/voice-recording/hooks/use-deepgram';
import { useMicrophone } from '@/features/voice-recording/hooks/use-microphone';
import { useVoiceRecording } from '@/lib/contexts/voice-recording-context';
import { CheckIcon, MicIcon, XIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef } from 'react';
import Text from '../ui/text';

type VoiceRecordingProps = {
    onInput: (text: string) => void;
    onRecordingStateChange?: (isRecording: boolean) => void;
};

export const VoiceRecording = ({ onInput, onRecordingStateChange }: VoiceRecordingProps) => {
    const sessionId = useId();
    const { currentSessionId, startSession, endSession } = useVoiceRecording();
    const sendEvent = useAnalytics();

    const isCanceledRef = useRef(false);

    const canStartRecording = currentSessionId === null;

    const { isTranscribing, transcribe } = useDeepgram((text) => {
        if (text) {
            onInput(text);
        }
        endSession(sessionId);
    });

    const { isRecording, startRecording, stopRecording, timeSinceStarted } = useMicrophone(
        (audioBlob, duration) => {
            if (isCanceledRef.current && audioBlob.size > 0) {
                endSession(sessionId);
                return;
            } else {
                transcribe({
                    blob: audioBlob,
                    duration,
                });
            }
        },
    );

    useEffect(() => {
        onRecordingStateChange?.(isRecording);
    }, [isRecording, onRecordingStateChange]);

    const recordingTimeString = useMemo(() => {
        const seconds = Math.floor(timeSinceStarted / 1000);

        return `${Math.floor(seconds / 60)
            .toString()
            .padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
    }, [timeSinceStarted]);

    const handleStartRecording = () => {
        if (!currentSessionId) {
            startSession(sessionId);
            isCanceledRef.current = false;
            startRecording();
            sendEvent('voice_input_used');
        }
    };

    if (!isRecording) {
        return (
            <Button
                variant="secondary"
                icon={MicIcon}
                size="sm"
                isLoading={isTranscribing}
                onClick={handleStartRecording}
                disabled={!canStartRecording || isTranscribing}
            />
        );
    }

    const handleCancelRecording = () => {
        isCanceledRef.current = true;
        stopRecording();
    };

    return (
        <div className="flex gap-1 items-center">
            <div className="pr-1">
                <Text>{recordingTimeString}</Text>
            </div>
            <Button variant="subtle" icon={XIcon} size="sm" onClick={handleCancelRecording} />
            <Button variant="primary" icon={CheckIcon} size="sm" onClick={stopRecording} />
        </div>
    );
};
